import { z } from "zod";

export const valueFormatSchema = z.enum([
  "number",
  "compactNumber",
  "currency",
  "percent",
  "duration",
  "date",
  "string",
]);

const chartColorSchema = z.enum([
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
]);

const cardToneSchema = z.enum([
  "default",
  "success",
  "warning",
  "danger",
  "info",
]);

const sortSchema = z.enum(["asc", "desc"]).default("desc");

const gridSpanSchema = z.number().int().min(1).max(12);

export const tabbedBarListTabSchema = z.object({
  label: z.string(),
  queryId: z.string(),
  nameKey: z.string().default("label"),
  valueKey: z.string().default("sessions"),
  valueLabel: z.string().default("Sessions"),
  secondaryValueKey: z.string().nullish(),
  secondaryLabel: z.string().nullish(),
  format: valueFormatSchema.default("compactNumber"),
});

export const insightsComponents = {
  DashboardShell: {
    props: z.object({
      title: z.string().min(1),
      description: z.string().nullish(),
    }),
    slots: ["default"],
    description:
      "Top-level wrapper for a dashboard page. Contains other Section/Grid children.",
  },

  PageHeader: {
    props: z.object({
      title: z.string().min(1),
      description: z.string().nullish(),
      badges: z
        .array(
          z.object({
            label: z.string(),
            value: z.string(),
            tone: cardToneSchema.default("default"),
          }),
        )
        .nullish(),
    }),
    slots: ["default"],
    description: "Page header with title, description, and optional badges.",
  },

  Section: {
    props: z.object({
      title: z.string().nullish(),
      description: z.string().nullish(),
      padding: z.enum(["none", "sm", "md"]).default("md"),
    }),
    slots: ["default"],
    description: "Vertical section with title and child content.",
  },

  TextBlock: {
    props: z.object({
      text: z.string().min(1).max(1000),
      variant: z.enum(["body", "muted", "caption"]).default("body"),
      align: z.enum(["left", "center", "right"]).default("left"),
    }),
    slots: [],
    description:
      "Standalone static text block. Use sparingly for short narrative context or methodology notes.",
  },

  Grid: {
    props: z.object({
      gap: z.enum(["sm", "md", "lg"]).default("md"),
      spans: z.array(gridSpanSchema).nullish(),
    }),
    slots: ["default"],
    description:
      "Responsive 12-track grid. Children set `span` (1-12, default 12 = full width); the grid lays them out and collapses by container width.",
  },

  MetricCard: {
    props: z.object({
      label: z.string(),
      queryId: z.string(),
      valueKey: z.string(),
      format: valueFormatSchema.default("number"),
      emptyValue: z.string().default("--"),
      description: z.string().nullish(),
      tone: cardToneSchema.default("default"),
    }),
    slots: [],
    description:
      "Single big-number KPI from the first row of a query. Use for headline / live metrics.",
  },

  DeltaMetricCard: {
    props: z.object({
      label: z.string(),
      queryId: z.string(),
      currentKey: z.string().default("current"),
      previousKey: z.string().default("previous"),
      format: valueFormatSchema.default("number"),
      invertColor: z.boolean().default(false),
      description: z.string().nullish(),
    }),
    slots: [],
    description:
      "KPI with a percentage delta vs. a previous period. Query must return one row with `current` and `previous` columns.",
  },

  UsageBar: {
    props: z.object({
      label: z.string(),
      queryId: z.string(),
      valueKey: z.string(),
      capacityKey: z.string().default("capacity"),
      format: valueFormatSchema.default("number"),
      description: z.string().nullish(),
    }),
    slots: [],
    description:
      "Progress bar showing `value / capacity` from the first query row. Useful for usage / quota cards.",
  },

  TimeSeriesChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      xKey: z.string().default("day"),
      series: z
        .array(
          z.object({
            key: z.string(),
            label: z.string(),
            color: z.string().nullish(),
            dashed: z.boolean().default(false),
            backgroundArea: z.boolean().default(false),
          }),
        )
        .min(1),
      chart: z.enum(["line", "area", "bar"]).default("line"),
      format: valueFormatSchema.default("number"),
      showYAxis: z.boolean().default(true),
      height: z.number().int().min(120).max(600).default(260),
    }),
    slots: [],
    description:
      "Daily/hourly time series. Each `series.key` is a column on the query result, each row is one bucket. `showYAxis` (default true) renders numeric tick labels on the left.",
  },

  BarChartCard: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      xKey: z.string().default("name"),
      yKey: z.string().default("value"),
      format: valueFormatSchema.default("number"),
      layout: z.enum(["vertical", "horizontal"]).default("vertical"),
      showYAxis: z.boolean().default(true),
      height: z.number().int().min(120).max(600).default(260),
    }),
    slots: [],
    description:
      "Bar chart for categorical comparisons. Use `layout: 'horizontal'` for top-N lists. `showYAxis` (default true) shows numeric ticks for vertical layout; horizontal layout always shows category labels on the left.",
  },

  BarList: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      nameKey: z.string().default("name"),
      valueKey: z.string().default("value"),
      format: valueFormatSchema.default("number"),
      sort: sortSchema,
      limit: z.number().int().min(1).max(100).default(10),
    }),
    slots: [],
    description:
      "Stacked horizontal bar list. Great for top-N pages, sources, products.",
  },

  DataTable: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      columns: z
        .array(
          z.object({
            key: z.string(),
            label: z.string(),
            format: valueFormatSchema.default("string"),
            align: z.enum(["left", "right", "center"]).default("left"),
            cell: z
              .enum(["text", "bar", "badge", "delta", "heatmap", "sparkline"])
              .default("text"),
            icon: z.boolean().default(false),
            sortable: z.boolean().default(true),
            deltaKey: z.string().nullish(),
            invertDelta: z.boolean().default(false),
            color: chartColorSchema.nullish(),
            hideable: z.boolean().default(true),
            defaultHidden: z.boolean().default(false),
          }),
        )
        .min(1),
      limit: z.number().int().min(1).max(500).default(50),
      search: z.boolean().default(true),
      columnToggle: z.boolean().default(true),
      barKey: z.string().nullish(),
      defaultSort: z
        .object({ key: z.string(), direction: sortSchema })
        .nullish(),
    }),
    slots: [],
    description:
      "Tabular view of query rows with sortable headers, an optional proportional row bar, and rich per-column cells (text+icon, inline bar, badge, delta, heatmap, sparkline). Capable of OpenPanel-style ranked and session tables.",
  },

  TabbedBarList: {
    props: z.object({
      title: z.string().nullish(),
      tabs: z.array(tabbedBarListTabSchema).min(1),
      limit: z.number().int().min(1).max(100).default(8),
    }),
    slots: [],
    description:
      "Tabbed ranked-list panel. Each tab switches the dimension/query; rows show a name, a proportional bar, and up to two numeric columns. Use for Referrers/Sources/Pages/Events breakdowns where the user toggles dimensions in place.",
  },

  Funnel: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      steps: z
        .array(
          z.object({
            key: z.string(),
            label: z.string(),
          }),
        )
        .min(2),
      format: valueFormatSchema.default("number"),
      orientation: z.enum(["horizontal", "vertical"]).default("horizontal"),
      edges: z.enum(["curved", "straight"]).default("curved"),
      layers: z.number().int().min(1).max(6).default(3),
      gap: z.number().min(0).max(24).default(4),
      staggerDelay: z.number().min(0).max(1).default(0.12),
      showValues: z.boolean().default(true),
      showLabels: z.boolean().default(true),
      showPercentage: z.boolean().default(true),
      color: chartColorSchema.default("var(--chart-1)"),
    }),
    slots: [],
    description:
      "Conversion funnel rendered from one row whose columns are step counts (e.g. viewed → added_to_cart → purchased). Multi-layer animated funnel; styling props are optional.",
  },

  PieChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      nameKey: z.string().default("name"),
      valueKey: z.string().default("value"),
      format: valueFormatSchema.default("number"),
      variant: z.enum(["pie", "donut"]).default("pie"),
      height: z.number().int().min(160).max(600).default(280),
    }),
    slots: [],
    description:
      "Pie/donut chart for part-to-whole comparisons across a handful of categories. One slice per query row.",
  },

  RingChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      nameKey: z.string().default("name"),
      valueKey: z.string().default("value"),
      height: z.number().int().min(160).max(600).default(280),
    }),
    slots: [],
    description:
      "Concentric progress rings (one per row), each showing its share of the combined total. Good for channel/source mix.",
  },

  GaugeChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      valueKey: z.string().default("value"),
      max: z.number().positive().default(100),
      format: valueFormatSchema.default("number"),
      height: z.number().int().min(160).max(600).default(220),
    }),
    slots: [],
    description:
      "Single-value gauge with a notched arc. Use for a score, utilization, or goal-completion metric (`value / max`).",
  },

  RadarChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      nameKey: z.string().default("name"),
      metrics: z
        .array(
          z.object({
            key: z.string(),
            label: z.string(),
          }),
        )
        .min(3),
      height: z.number().int().min(200).max(600).default(320),
    }),
    slots: [],
    description:
      "Radar/spider chart comparing several normalized (0-100) metrics. Each query row becomes one polygon across the metric axes.",
  },

  ScatterChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      xKey: z.string().default("day"),
      series: z
        .array(
          z.object({
            key: z.string(),
            label: z.string(),
            color: chartColorSchema.nullish(),
          }),
        )
        .min(1),
      format: valueFormatSchema.default("number"),
      showYAxis: z.boolean().default(true),
      height: z.number().int().min(160).max(600).default(260),
    }),
    slots: [],
    description:
      "Scatter plot of one or more numeric series against a time x-axis. Good for spotting clusters or outliers. `showYAxis` (default true) renders numeric tick labels on the left.",
  },

  HeatmapChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      columnKey: z.string().default("week"),
      binKey: z.string().default("weekday"),
      valueKey: z.string().default("value"),
      dateKey: z.string().default("date"),
      height: z.number().int().min(160).max(600).default(220),
    }),
    slots: [],
    description:
      "Calendar-style heatmap. Query rows must carry a column bucket, a row bin, a date, and a value (e.g. one row per day with week + weekday + date + count).",
  },

  SankeyChart: {
    props: z.object({
      title: z.string().nullish(),
      queryId: z.string(),
      sourceKey: z.string().default("source"),
      targetKey: z.string().default("target"),
      valueKey: z.string().default("value"),
      height: z.number().int().min(200).max(600).default(320),
    }),
    slots: [],
    description:
      "Sankey flow diagram. Each query row is an edge (`source` -> `target` with a `value`); nodes are derived from the distinct labels.",
  },

  InsightCallout: {
    props: z.object({
      title: z.string(),
      body: z.string(),
      tone: cardToneSchema.default("info"),
    }),
    slots: [],
    description:
      "Static narrative callout for context, methodology notes, or open questions.",
  },

  EmptyState: {
    props: z.object({
      title: z.string(),
      description: z.string().nullish(),
    }),
    slots: [],
    description:
      "Friendly placeholder for queries that returned zero rows. The renderer also shows a default empty state automatically.",
  },

  QueryErrorBoundary: {
    props: z.object({
      message: z.string(),
    }),
    slots: [],
    description:
      "Used by the renderer to surface query errors inline. Agents typically don't author this directly.",
  },
};
